
import { Component, Emit, InjectReactive, Vue } from 'vue-property-decorator'
import { Getter } from 'vuex-class';

import { OrderMethods, OrderSteps, TimeModes } from '@/enums';
import BasketModule from '@/store/modules/basket';
import { addHoursToDate, formatHour, formatPhoneNumber, hourMethodMap, indexToDayMap, parseDate } from '@/utils/global.util';

@Component
export default class Location extends Vue {
  @Getter customerId: string;
  @Getter activeRestaurant: Restaurant;
  @InjectReactive() isMobile!: boolean;

  @Emit('toggle-review-drawer')
  toggleReviewDrawer(ev) {
    return ev;
  }

  @Emit('show-login')
  showLogin(ev) {
    return ev;
  }

  orderMethods = OrderMethods;

  get cartSize() {
    return this.isMobile ? 25 : 60;
  }

  get deliveryMode() {
    return BasketModule.deliveryMode;
  }

  get usedDeliveryMode() {
    return BasketModule.deliveryMode === OrderMethods.DISPATCH ? OrderMethods.DELIVERY : BasketModule.deliveryMode;
  }

  get timeMode() {
    return BasketModule.timeMode;
  }

  get timeModeText() {
    return this.timeMode === TimeModes.ADVANCE ? 'scheduled' : this.timeMode;
  }

  get timeModeClass() {
    return {
      capitalize: this.timeMode === TimeModes.ADVANCE,
      uppercase: this.timeMode === TimeModes.ASAP,
    }
  }

  get phoneNumber() {
    return formatPhoneNumber(this.activeRestaurant?.phoneNumber || '');
  }

  get dayInt() {
    return new Date().getDay();
  }

  get hoursForDayAndMethod(): Period {
    return this.activeRestaurant?.hours?.find((hour) => hour.type === hourMethodMap.get(this.deliveryMode || '') && (hour.day === indexToDayMap.get(this.dayInt))) as Period;
  }

  get formattedHour() {
    return Object.keys(this.hoursForDayAndMethod || {}).length ? `${formatHour(this.hoursForDayAndMethod.from)} - ${formatHour(this.hoursForDayAndMethod.to)}` : '';
  }

  get orderSteps() {
    return Object.values(OrderSteps || {});
  }

  get stepInfo(): OrderStep {
    const routeName = this.$route?.name;
    return (this.orderSteps?.find(({ view, aliasViews = [] }) => {
      return view === routeName || aliasViews?.includes(routeName as string);
    })) || {} as OrderStep;
  }

  get address() {
    const { building, streetAddress } = BasketModule.current?.deliveryAddress || {};
    return `${streetAddress}${building ? `, ${building}` : ''}`;
  }

  get hideCartIfEmpty() {
    return !(this.stepInfo?.hideCartIfEmpty && !BasketModule.hasProducts);
  }

  get restaurantName() {
    return this.activeRestaurant.name || '';
  }

  get currentBasket() {
    return BasketModule.current;
  }

  get readyTime() {
    return (this.currentBasket?.timeMode === 'advance' ? this.currentBasket?.timeWanted : this.currentBasket?.earliestReadyTime)
  }

  get today(): Date {
    // return new Date('2021-09-11T12:10:00'); // used to spoof times for testing
    return new Date();
  }

  get tomorrow(): Date {
    return addHoursToDate(this.today, 24);
  }

  get todayValue(): string {
    return `${this.today.getFullYear()}-${this.today.getMonth() + 1}-${this.today.getDate()}`;
  }

  get tomorrowValue(): string {
    return `${this.tomorrow.getFullYear()}-${this.tomorrow.getMonth() + 1}-${this.tomorrow.getDate()}`;
  }

  get readyTimeText() {
    if (this.readyTime) {
      const parsedDate = parseDate(this.readyTime) as Date;
      const parsedDateValue = `${parsedDate.getFullYear()}-${parsedDate.getMonth() + 1}-${parsedDate.getDate()}`;
      if (parsedDateValue === this.todayValue) {
        return `Ready at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (parsedDateValue === this.tomorrowValue) {
        return `Ready tomorrow at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return `Ready ${parsedDate.getMonth() + 1}/${parsedDate.getDate()} at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      }
    }
    return null;
  }
}
